import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { parseSmartInput } from '../lib/smartInputParser';
import { API_BASE_URL } from '../config';

/**
 * SmartInput v1.1 - The "Omnibar" component
 * @param {Function} onAdd - Called when an item is confirmed
 * @param {string} mode - 'quick' | 'wizard'
 */
const SmartInput = ({ onAdd, mode = 'quick', onSelectSuggestion = null }) => {
    const [inputValue, setInputValue] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [isLoading, setIsLoading] = useState(false);
    const activeFetchRef = useRef(0);

    useEffect(() => {
        const trimmedValue = inputValue.trim();

        if (trimmedValue.length < 2) {
            setSuggestions([]);
            setSelectedIndex(-1);
            setIsLoading(false);
            return;
        }

        const timer = setTimeout(() => {
            fetchSuggestions(trimmedValue);
        }, 80);

        return () => clearTimeout(timer);
    }, [inputValue]);

    const fetchSuggestions = async (query) => {
        const fetchId = Date.now();
        activeFetchRef.current = fetchId;

        try {
            setIsLoading(true);
            const res = await fetch(`${API_BASE_URL}/api/inventory/search-ontology?q=${encodeURIComponent(query)}`);
            const data = await res.json();

            if (activeFetchRef.current !== fetchId) return;

            const nextSuggestions = Array.isArray(data) ? data.slice(0, 8) : [];
            setSuggestions(nextSuggestions);
            setSelectedIndex(nextSuggestions.length > 0 && mode === 'wizard' ? 0 : -1);
        } catch (err) {
            if (activeFetchRef.current === fetchId) {
                console.error('Autocomplete Error:', err);
                setSuggestions([]);
                setSelectedIndex(-1);
            }
        } finally {
            if (activeFetchRef.current === fetchId) {
                setIsLoading(false);
            }
        }
    };

    const resetInputState = () => {
        setInputValue('');
        setSuggestions([]);
        setSelectedIndex(-1);
    };

    const handleAdd = (suggestion = null) => {
        const text = inputValue.trim();
        if (!text && !suggestion) return;

        const parsed = parseSmartInput(text);
        const preferredSuggestion = suggestion || (mode === 'wizard' && suggestions.length > 0 ? suggestions[0] : null);

        const item = {
            name: preferredSuggestion ? preferredSuggestion.name : parsed.name,
            amount: parsed.amount,
            unit: parsed.unit || preferredSuggestion?.unit || null,
            dictionaryId: preferredSuggestion ? preferredSuggestion.id : null,
            resolveStatus: preferredSuggestion ? 'RESOLVED' : 'UNKNOWN',
            defaultPrice: preferredSuggestion?.defaultPrice || 0,
            defaultPriceUnit: preferredSuggestion?.defaultPriceUnit || '',
            defaultPriceSource: preferredSuggestion?.defaultPriceSource || 'NONE',
            requiresBrand: Boolean(preferredSuggestion?.requiresBrand)
        };

        onAdd(item);
        resetInputState();
    };

    const handleSuggestionSelect = (suggestion) => {
        if (mode === 'wizard' && typeof onSelectSuggestion === 'function') {
            onSelectSuggestion(suggestion, {
                text: inputValue.trim(),
                parsed: parseSmartInput(inputValue.trim())
            });
            resetInputState();
            return;
        }

        handleAdd(suggestion);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex((prev) => (prev > -1 ? prev - 1 : prev));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedIndex >= 0) {
                handleSuggestionSelect(suggestions[selectedIndex]);
            } else {
                handleAdd();
            }
        } else if (e.key === 'Escape') {
            setSuggestions([]);
        }
    };

    return (
        <div className="relative w-full">
            <div className="relative flex items-center group">
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={mode === 'quick' ? 'Örn: 2 kg domates...' : 'Ürün ara veya yaz...'}
                    className={`w-full h-14 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-800 rounded-2xl text-sm font-bold focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all placeholder:text-gray-400 dark:placeholder:text-gray-600 ${
                        mode === 'wizard' ? 'pl-5 pr-5' : 'pl-5 pr-14'
                    }`}
                />

                {mode !== 'wizard' && (
                    <button
                        onClick={() => handleAdd()}
                        className="absolute right-2 w-10 h-10 bg-primary hover:bg-red-600 text-white rounded-xl flex items-center justify-center shadow-lg shadow-primary/20 active:scale-95 transition-all"
                    >
                        <span className="material-symbols-outlined text-xl">add</span>
                    </button>
                )}

                {isLoading && (
                    <div
                        className={`absolute animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full ${
                            mode === 'wizard' ? 'right-4' : 'right-14'
                        }`}
                    />
                )}
            </div>

            <AnimatePresence>
                {suggestions.length > 0 && (
                    mode === 'wizard' ? (
                        <motion.div
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 5 }}
                            className="mt-3 flex flex-wrap gap-2"
                        >
                            {suggestions.map((suggestion, idx) => (
                                <button
                                    key={`${suggestion.id}-${idx}`}
                                    onClick={() => handleSuggestionSelect(suggestion)}
                                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-[11px] font-black transition-all ${
                                        selectedIndex === idx
                                            ? 'border-primary bg-primary text-white shadow-lg shadow-primary/20'
                                            : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-primary/40 hover:bg-primary/5'
                                    }`}
                                >
                                    <span className="material-symbols-outlined text-[16px]">
                                        {suggestion.type === 'FAMILY' ? 'inventory_2' : 'shopping_basket'}
                                    </span>
                                    <span>{suggestion.name}</span>
                                    {suggestion.unit && (
                                        <span
                                            className={`rounded-full px-2 py-0.5 text-[10px] ${
                                                selectedIndex === idx ? 'bg-white/20 text-white' : 'bg-white text-gray-500'
                                            }`}
                                        >
                                            {suggestion.unit}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </motion.div>
                    ) : (
                        <motion.div
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 5 }}
                            className="absolute z-[100] w-full mt-2 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-2xl shadow-2xl overflow-hidden"
                        >
                            {suggestions.map((suggestion, idx) => (
                                <button
                                    key={`${suggestion.id}-${idx}`}
                                    onClick={() => handleSuggestionSelect(suggestion)}
                                    className={`w-full px-5 py-3 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                                        selectedIndex === idx ? 'bg-primary/5 dark:bg-primary/10 border-l-4 border-primary' : ''
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="material-symbols-outlined text-gray-400 text-lg">
                                            {suggestion.type === 'FAMILY' ? 'inventory_2' : 'shopping_basket'}
                                        </span>
                                        <div>
                                            <div className="text-sm font-black dark:text-white uppercase tracking-tight">
                                                {suggestion.name}
                                            </div>
                                            <div className="text-[10px] text-gray-400 uppercase font-bold">
                                                {suggestion.family} {suggestion.type === 'FAMILY' ? '• TÜM AİLE' : ''}
                                            </div>
                                        </div>
                                    </div>
                                    {suggestion.unit && (
                                        <span className="text-[10px] font-black px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-500 rounded-lg uppercase">
                                            {suggestion.unit}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </motion.div>
                    )
                )}
            </AnimatePresence>
        </div>
    );
};

export default SmartInput;
